import { Price } from 'shared/types/price'
import {
  convertAmountToCents,
  convertCentsToAmount,
  formatCurrency
} from 'shared/utils/currency'

import { Cart, CartItem,PackingOption  } from './types'

export const calculateItemValues = (items: CartItem[]) => {
  let totalGross = 0
  let totalNet = 0
  let totalVat = 0
  let totalSavings = 0

  const itemsWithValues = items.map(item => {
    const hasSpecialOffer =
      item.specialOfferPriceGross &&
      item.specialOfferPriceGross.value !== null &&
      item.specialOfferPriceGross.value < item.priceGross.value

    const normalPriceGross =
      convertCentsToAmount(item.priceGross.value) * item.quantity
    const normalPriceNet =
      convertCentsToAmount(item.priceNet.value) * item.quantity

    const specialOfferPriceGross = hasSpecialOffer
      ? convertCentsToAmount(item.specialOfferPriceGross!.value) * item.quantity
      : null
    const specialOfferPriceNet = hasSpecialOffer
      ? convertCentsToAmount(item.specialOfferPriceNet!.value) * item.quantity
      : null

    const itemGrossValue = hasSpecialOffer
      ? convertCentsToAmount(item.specialOfferPriceGross!.value)
      : convertCentsToAmount(item.priceGross.value)

    const itemNetValue = hasSpecialOffer
      ? convertCentsToAmount(item.specialOfferPriceNet!.value)
      : convertCentsToAmount(item.priceNet.value)

    const itemGross = itemGrossValue * item.quantity
    const itemNet = itemNetValue * item.quantity
    const itemVat = itemGross - itemNet

    totalGross += itemGross
    totalNet += itemNet
    totalVat += itemVat

    if (hasSpecialOffer) {
      const regularPrice = convertCentsToAmount(item.priceGross.value)
      const specialPrice = convertCentsToAmount(
        item.specialOfferPriceGross!.value
      )

      totalSavings += (regularPrice - specialPrice) * item.quantity
    }

    return {
      ...item,
      specialOfferValueGross:
        hasSpecialOffer && specialOfferPriceGross
          ? {
              ...item.specialOfferPriceGross,
              value: specialOfferPriceGross,
              formatted: formatCurrency(
                specialOfferPriceGross,
                item.specialOfferPriceGross!.symbol
              )
            }
          : null,
      specialOfferValueNet:
        hasSpecialOffer && specialOfferPriceNet
          ? {
              ...item.specialOfferPriceNet,
              value: specialOfferPriceNet,
              formatted: formatCurrency(
                specialOfferPriceNet,
                item.specialOfferPriceNet!.symbol
              )
            }
          : null,
      valueGross: {
        ...item.valueGross,
        value: normalPriceGross,
        formatted: formatCurrency(normalPriceGross, item.valueGross.symbol)
      },
      valueNet: {
        ...item.valueNet,
        value: normalPriceNet,
        formatted: formatCurrency(normalPriceNet, item.valueNet.symbol)
      },
      valueVat: {
        ...item.valueVat,
        value: itemVat,
        formatted: formatCurrency(itemVat, item.valueVat.symbol)
      }
    }
  })

  return { totalGross, totalNet, totalVat, totalSavings, itemsWithValues }
}

export const calculateDiscountAmount = (cart: Cart, totalGross: number) => {
  let discountAmount = 0

  if (cart.discountCodes && cart.discountCodes.length > 0) {
    const discountCode = cart.discountCodes[0]

    if (discountCode && discountCode.value) {
      if (discountCode.type === 'percentage') {
        const percentage = discountCode.value.value

        discountAmount = totalGross * (percentage / 100)
      } else {
        discountAmount = convertCentsToAmount(discountCode.value.value)
      }

      discountAmount = Math.min(discountAmount, totalGross)
    }
  }

  return discountAmount
}

export const recalculateCartTotals = (cartToUpdate: Cart): Cart => {
  if (
    !cartToUpdate ||
    !cartToUpdate.items ||
    !Array.isArray(cartToUpdate.items)
  ) {
    return cartToUpdate
  }

  const newCart = { ...cartToUpdate }
  const { totalGross, totalNet, totalVat, itemsWithValues } =
    calculateItemValues(cartToUpdate.items)
  const discountAmount = calculateDiscountAmount(cartToUpdate, totalGross)

  if (newCart.itemsValue) {
    newCart.itemsValue.gross = {
      value: convertAmountToCents(totalGross),
      formatted: formatCurrency(
        totalGross,
        cartToUpdate.itemsValue.gross.symbol
      ),
      symbol: cartToUpdate.itemsValue.gross.symbol
    }
    newCart.itemsValue.net = {
      value: convertAmountToCents(totalNet),
      formatted: formatCurrency(totalNet, cartToUpdate.itemsValue.net.symbol),
      symbol: cartToUpdate.itemsValue.net.symbol
    }
    newCart.itemsValue.vat = {
      value: convertAmountToCents(totalVat),
      formatted: formatCurrency(totalVat, cartToUpdate.itemsValue.vat.symbol),
      symbol: cartToUpdate.itemsValue.vat.symbol
    }
  }

  if (newCart.discountCodesTotalValue && discountAmount > 0) {
    newCart.discountCodesTotalValue = {
      value: convertAmountToCents(discountAmount),
      formatted: formatCurrency(
        discountAmount,
        newCart.discountCodesTotalValue.symbol
      ),
      symbol: newCart.discountCodesTotalValue.symbol
    }
  }

  if (newCart.totalValueGross) {
    const deliveryCost = convertCentsToAmount(newCart.deliveryCost?.value || 0)
    const totalWithDelivery = totalGross + deliveryCost - discountAmount

    newCart.totalValueGross = {
      value: convertAmountToCents(totalWithDelivery),
      formatted: formatCurrency(
        totalWithDelivery,
        cartToUpdate.totalValueGross.symbol
      ),
      symbol: cartToUpdate.totalValueGross.symbol
    }

    if (newCart.paymentValue) {
      newCart.paymentValue = {
        value: convertAmountToCents(totalWithDelivery),
        formatted: formatCurrency(
          totalWithDelivery,
          cartToUpdate.paymentValue.symbol
        ),
        symbol: cartToUpdate.paymentValue.symbol
      }
    }
  }

  if (newCart.totalValueNet) {
    newCart.totalValueNet = {
      value: convertAmountToCents(totalNet),
      formatted: formatCurrency(totalNet, cartToUpdate.totalValueNet.symbol),
      symbol: cartToUpdate.totalValueNet.symbol
    }
  }

  if (newCart.totalValueVat) {
    newCart.totalValueVat = {
      value: convertAmountToCents(totalVat),
      formatted: formatCurrency(totalVat, cartToUpdate.totalValueVat.symbol),
      symbol: cartToUpdate.totalValueVat.symbol
    }
  }

  if (itemsWithValues) {
    newCart.items = itemsWithValues as CartItem[]
  }

  return newCart
}

export const recalculateCartTotalsWithPackingOption = (
  cart: Cart,
  packingOption: PackingOption
): Cart => {
  const newCart = { ...cart }

  const currentPackingValue = cart.package?.price.gross.value || 0
  const paymentValue =
    cart.paymentValue.value +
    packingOption.price.gross.value -
    currentPackingValue

  const totalGrossValue =
    cart.totalValueGross.value +
    packingOption.price.gross.value -
    currentPackingValue

  const totalNetValue =
    cart.totalValueNet.value +
    packingOption.price.net.value -
    currentPackingValue

  const totalVatValue =
    cart.totalValueVat.value +
    packingOption.price.vat.value -
    currentPackingValue

  newCart.paymentValue = {
    value: paymentValue,
    formatted: formatCurrency(
      convertCentsToAmount(paymentValue),
      cart.paymentValue.symbol
    ),
    symbol: cart.paymentValue.symbol
  }

  newCart.totalValueGross = {
    value: totalGrossValue,
    formatted: formatCurrency(
      convertCentsToAmount(totalGrossValue),
      cart.totalValueGross.symbol
    ),
    symbol: cart.totalValueGross.symbol
  }

  newCart.totalValueNet = {
    value: totalNetValue,

    formatted: formatCurrency(
      convertCentsToAmount(totalNetValue),
      cart.totalValueNet.symbol
    ),
    symbol: cart.totalValueNet.symbol
  }

  newCart.totalValueVat = {
    value: totalVatValue,

    formatted: formatCurrency(
      convertCentsToAmount(totalVatValue),
      cart.totalValueVat.symbol
    ),
    symbol: cart.totalValueVat.symbol
  }

  newCart.package = packingOption

  return newCart
}

interface GetCartItemsPriceProps {
  paymentValue: Price | undefined
  deliveryPrice: Price | undefined
}

export const getCartItemsPrice = ({
  paymentValue,
  deliveryPrice
}: GetCartItemsPriceProps) => {
  const paymentValueValue = paymentValue?.value || 0
  const deliveryPriceValue = deliveryPrice?.value || 0

  return paymentValueValue - deliveryPriceValue
}
